import React from "react";

/**
 * SwitchControl, the signature SWITCH mechanism, drawn in CSS so it renders
 * reliably everywhere and animates smoothly between states.
 * OFF: charcoal track, warm-white knob (left). ON: lime track, charcoal knob (right).
 * Accessible: role="switch", keyboard operable, announces its state.
 */
const SIZES = {
  sm: { w: 60, h: 30, pad: 3, labelCls: "text-xs" },
  md: { w: 84, h: 42, pad: 4, labelCls: "text-sm" },
  lg: { w: 108, h: 54, pad: 5, labelCls: "text-sm" },
  xl: { w: 232, h: 112, pad: 10, labelCls: "text-base" },
};

export const SwitchControl = ({
  active = false,
  onToggle,
  ariaLabel = "SWITCH mechanism",
  offLabel,
  onLabel,
  size = "md",
  tone = "light",
  className = "",
  testId = "switch-control",
}) => {
  const s = SIZES[size] || SIZES.md;
  const knob = s.h - s.pad * 2;
  const idleTone = tone === "dark" ? "text-warm-white/45" : "text-charcoal/40";
  const activeTone = tone === "dark" ? "text-lime" : "text-deep-green";

  const playTick = (turningOn) => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = (playTick._ctx = playTick._ctx || new AC());
      if (ctx.state === "suspended") ctx.resume();
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(turningOn ? 660 : 420, now);
      osc.frequency.exponentialRampToValueAtTime(turningOn ? 880 : 300, now + 0.05);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.06, now + 0.008);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.14);
    } catch (e) { /* audio not available */ }
  };

  const handleClick = () => {
    playTick(!active);
    if (onToggle) onToggle(!active);
  };

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {offLabel && (
        <span
          aria-hidden="true"
          className={`${s.labelCls} font-medium transition-colors duration-300 ${
            active ? idleTone : activeTone
          }`}
        >
          {offLabel}
        </span>
      )}

      <button
        type="button"
        role="switch"
        aria-checked={active}
        aria-label={ariaLabel}
        data-testid={testId}
        onClick={handleClick}
        className="focus-ring relative shrink-0 rounded-full transition-transform duration-200 ease-switch active:scale-95"
        style={{
          width: s.w,
          height: s.h,
          padding: s.pad,
          backgroundColor: active ? "#5f8f10" : "#282828",
          border: tone === "dark" ? "1px solid rgba(245,242,234,0.18)" : "1px solid rgba(40,40,40,0.12)",
          boxShadow: active ? "0 0 0 4px rgba(155,224,23,0.22)" : "none",
          transition: "background-color 0.35s ease, box-shadow 0.35s ease, transform 0.2s ease",
        }}
      >
        <span
          className="block rounded-full"
          style={{
            width: knob,
            height: knob,
            backgroundColor: active ? "#282828" : "#F5F2EA",
            transform: active ? `translateX(${s.w - knob - s.pad * 2}px)` : "translateX(0)",
            transition: "transform 0.4s cubic-bezier(0.22,1,0.36,1), background-color 0.35s ease",
          }}
        />
      </button>

      {onLabel && (
        <span
          aria-hidden="true"
          className={`${s.labelCls} font-medium transition-colors duration-300 ${
            active ? activeTone : idleTone
          }`}
        >
          {onLabel}
        </span>
      )}
    </div>
  );
};

export default SwitchControl;
